import { apiClient } from "@/shared/api/api-client";
import { ApiError } from "@/shared/api/http";

/**
 * Logos da empresa (2026-10-16): além de SVG, a tela aceita PNG, JPG e WebP,
 * e busca a logo no Logo.dev pelo domínio. O servidor guarda a logo como
 * TEXTO SVG (`web_api/application/company_profile.py::validate_logo`, teto de
 * 512 KB e recusa de script/evento) — então toda imagem bitmap é reduzida no
 * navegador e embrulhada num `<svg>` com a imagem dentro (`<image href=
 * "data:...">`). Nada muda do lado do servidor.
 *
 * O embrulho continua bitmap por dentro: escala como imagem, não como vetor.
 * Por isso a redução mira o DOBRO da referência de 417×128 (tela retina).
 */

/** O tamanho máximo do desenho: o dobro de 417×128, pra ficar nítido em retina. */
const MAX_WIDTH = 834;
const MAX_HEIGHT = 256;
/** Abaixo do teto de 512 KB do servidor, com folga pro markup do embrulho. */
const MAX_SVG_BYTES = 480 * 1024;

export const LOGO_ACCEPT = ".svg,.png,.jpg,.jpeg,.webp,image/svg+xml,image/png,image/jpeg,image/webp";
export const LOGO_FORMAT_ERROR = "Formato não suportado. Envie um arquivo .svg, .png, .jpg ou .webp.";

export class LogoImageError extends Error {}

export function isSvgFile(file: File): boolean {
  return file.name.toLowerCase().endsWith(".svg") || file.type === "image/svg+xml";
}

export function isRasterFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return (
    /\.(png|jpe?g|webp)$/.test(name) ||
    file.type === "image/png" ||
    file.type === "image/jpeg" ||
    file.type === "image/webp"
  );
}

async function decode(blob: Blob): Promise<{ width: number; height: number; draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void; close: () => void }> {
  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(blob);
    return {
      width: bitmap.width,
      height: bitmap.height,
      draw: (ctx, w, h) => ctx.drawImage(bitmap, 0, 0, w, h),
      close: () => bitmap.close()
    };
  }
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new LogoImageError("Não foi possível ler a imagem."));
      image.src = url;
    });
    return {
      width: img.naturalWidth,
      height: img.naturalHeight,
      draw: (ctx, w, h) => ctx.drawImage(img, 0, 0, w, h),
      close: () => URL.revokeObjectURL(url)
    };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
}

/** Escala de cinza pelo peso de luminância (preserva a transparência). */
function toGrayscale(ctx: CanvasRenderingContext2D, width: number, height: number) {
  const image = ctx.getImageData(0, 0, width, height);
  const data = image.data;
  for (let i = 0; i < data.length; i += 4) {
    const gray = Math.round(0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]);
    data[i] = data[i + 1] = data[i + 2] = gray;
  }
  ctx.putImageData(image, 0, 0);
}

function svgWrapper(dataUrl: string, width: number, height: number): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}"><image href="${dataUrl}" width="${width}" height="${height}"/></svg>`;
}

/**
 * Uma imagem bitmap (arquivo ou o que veio do Logo.dev) vira o SVG que o
 * servidor aceita: reduzida pra caber em 834×256 sem distorcer, em WebP (com
 * queda pra PNG onde o navegador não codifica WebP), em cinza se pedido, e
 * menor que o teto do servidor — se não couber, diminui mais.
 */
export async function rasterToLogoSvg(blob: Blob, options: { grayscale?: boolean } = {}): Promise<string> {
  const source = await decode(blob);
  try {
    if (!source.width || !source.height) throw new LogoImageError("A imagem está vazia.");
    let scale = Math.min(1, MAX_WIDTH / source.width, MAX_HEIGHT / source.height);
    for (let attempt = 0; attempt < 4; attempt++) {
      const width = Math.max(1, Math.round(source.width * scale));
      const height = Math.max(1, Math.round(source.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new LogoImageError("Este navegador não consegue processar a imagem.");
      source.draw(ctx, width, height);
      if (options.grayscale) toGrayscale(ctx, width, height);
      let dataUrl = canvas.toDataURL("image/webp", 0.9);
      if (!dataUrl.startsWith("data:image/webp")) dataUrl = canvas.toDataURL("image/png");
      const svg = svgWrapper(dataUrl, width, height);
      if (new Blob([svg]).size <= MAX_SVG_BYTES) return svg;
      scale *= 0.7;
    }
    throw new LogoImageError("A imagem é grande demais mesmo reduzida. Tente um arquivo menor.");
  } finally {
    source.close();
  }
}

// ── Logo.dev ────────────────────────────────────────────────────────────────

/** A chave PÚBLICA do Logo.dev (`pk_…`), feita pra ir no navegador. Sem ela,
 *  a busca não aparece na tela. */
export const LOGO_DEV_TOKEN = (import.meta.env.VITE_LOGO_DEV_TOKEN as string | undefined)?.trim() || null;

export const LOGO_RIGHTS_NOTICE =
  "Confirmo que a identidade corresponde à empresa e que há autorização para uso.";

/** O domínio como o Logo.dev espera: sem protocolo, sem "www.", sem caminho. */
export function normalizeDomain(input: string): string | null {
  const cleaned = input
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, "")
    .replace(/^www\./, "")
    .split(/[/?#]/)[0];
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(cleaned) ? cleaned : null;
}

export type BrandCandidate = { name: string; domain: string };

/**
 * Nome da marca → candidatos com domínio (2026-10-16). Primeiro na base do
 * Logo.dev, pelo servidor (`GET /companies/logo-search`: a busca exige a
 * chave SECRETA, que não pode ir pro navegador). Sem a chave configurada lá
 * (503), cai no autocompletar público de empresas, que responde sem chave
 * mas tem menos marcas. A logo de cada candidato vem sempre do Logo.dev.
 */
export async function searchBrandCandidates(query: string, signal?: AbortSignal): Promise<BrandCandidate[]> {
  const q = query.trim();
  if (q.length < 2) return [];
  let raw: unknown;
  try {
    raw = await apiClient.searchLogos("", q);
  } catch (cause) {
    if (!(cause instanceof ApiError && cause.status === 503)) {
      throw new LogoImageError("Não foi possível buscar marcas agora. Tente de novo.");
    }
    const response = await fetch(
      `https://autocomplete.clearbit.com/v1/companies/suggest?query=${encodeURIComponent(q)}`,
      { signal }
    );
    if (!response.ok) throw new LogoImageError("Não foi possível buscar marcas agora. Tente de novo.");
    raw = await response.json();
  }
  return toCandidates(raw);
}

function toCandidates(data: unknown): BrandCandidate[] {
  if (!Array.isArray(data)) return [];
  const seen = new Set<string>();
  const out: BrandCandidate[] = [];
  for (const item of data) {
    const name = typeof item?.name === "string" ? item.name.trim() : "";
    const domain = typeof item?.domain === "string" ? normalizeDomain(item.domain) : null;
    if (!domain || seen.has(domain)) continue;
    seen.add(domain);
    out.push({ name: name || domain, domain });
  }
  return out;
}

/** A URL da imagem do Logo.dev (sempre `https://img.logo.dev`, a única origem
 *  que o backend também aceitaria — `logo_providers.py`). */
export function logoDevUrl(domain: string, token: string): string {
  return `https://img.logo.dev/${encodeURIComponent(domain)}?token=${encodeURIComponent(token)}&size=256&format=png&retina=true`;
}

/** Baixa a logo do Logo.dev e a converte no SVG da empresa. */
export async function fetchLogoDevSvg(domain: string, token: string, options: { grayscale?: boolean } = {}): Promise<string> {
  let response: Response;
  try {
    response = await fetch(logoDevUrl(domain, token), { mode: "cors" });
  } catch {
    throw new LogoImageError("Não foi possível falar com o Logo.dev. Confira a conexão ou envie a logo pelo arquivo.");
  }
  if (!response.ok) {
    throw new LogoImageError(
      response.status === 404
        ? "O Logo.dev não tem logo para esse domínio."
        : "O Logo.dev recusou o pedido. Confira a chave e o domínio."
    );
  }
  return rasterToLogoSvg(await response.blob(), options);
}

/**
 * Um arquivo de logo escolhido na tela → o SVG que o servidor guarda.
 * SVG entra como está (se tiver a marcação `<svg>`); PNG, JPG e WebP são
 * reduzidos e embrulhados (`rasterToLogoSvg`). Recusa com `LogoImageError`
 * e mensagem pronta pra tela.
 */
export async function readLogoFile(file: File): Promise<string> {
  if (isSvgFile(file)) {
    let text: string;
    try {
      text = await file.text();
    } catch {
      throw new LogoImageError("Não foi possível ler o arquivo.");
    }
    if (!/<svg[\s>]/i.test(text)) throw new LogoImageError("O arquivo .svg não tem a marcação <svg>.");
    return text;
  }
  if (!isRasterFile(file)) throw new LogoImageError(LOGO_FORMAT_ERROR);
  return rasterToLogoSvg(file);
}
